const STORAGE_HEADROOM_MIN_FREE_BYTES = 128 * 1024 * 1024
const STORAGE_HEADROOM_TARGET_FREE_BYTES = 256 * 1024 * 1024
const STORAGE_HEADROOM_MAX_PRUNE_BUDGET_BYTES = 2 * 1024 * 1024 * 1024
const STORAGE_HEADROOM_ATTEMPT_COOLDOWN_MS = 120_000

type CacheCleanupClient = {
  cache_prune_storage?: (opts?: { budgetBytes?: number; activeDatasetIds?: string[] }) => Promise<any>
  cache_cleanup_orphan_parquet?: (opts?: { maxDepth?: number }) => Promise<any>
  cache_cleanup_zero_byte_files?: (opts?: { maxDepth?: number }) => Promise<any>
}

export type StorageHeadroomResult = {
  attempted: boolean
  cleaned: boolean
  quotaBytes: number | null
  usageBytes: number | null
  freeBytes: number | null
}

let storageHeadroomInFlight: Promise<StorageHeadroomResult> | null = null
let storageHeadroomLastAttemptAtMs = 0

let storagePersistInFlight: Promise<boolean> | null = null
let storagePersistResolvedAt = 0
const STORAGE_PERSIST_RETRY_COOLDOWN_MS = 10 * 60_000

/**
 * Request persistent-storage status early and unconditionally.
 *
 * Why: In Chrome the OPFS + IndexedDB quota granted to a non-persistent origin
 * can be as small as a few GiB (we've observed ~3 GiB on localhost), which is
 * not enough to materialize large `aggregate_base` TEMP TABLE plans that
 * UNPIVOT 1M+ rows. Persistent-storage origins get a much larger quota
 * (typically up to 60% of free disk) and are protected from eviction.
 *
 * Returns true if storage is (or becomes) persistent, false otherwise.
 * Cached per session to avoid spamming the permission API.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  if (storagePersistInFlight) return await storagePersistInFlight
  const nowMs = Date.now()
  if (storagePersistResolvedAt > 0 && (nowMs - storagePersistResolvedAt) < STORAGE_PERSIST_RETRY_COOLDOWN_MS) {
    return false
  }
  const run = async (): Promise<boolean> => {
    try {
      if (typeof navigator === 'undefined' || !navigator.storage) return false
      const existing = typeof navigator.storage.persisted === 'function'
        ? await navigator.storage.persisted().catch(() => false)
        : false
      if (existing) return true
      if (typeof navigator.storage.persist !== 'function') return false
      const granted = await navigator.storage.persist().catch(() => false)
      return Boolean(granted)
    } catch {
      return false
    }
  }
  storagePersistInFlight = run()
  try {
    const result = await storagePersistInFlight
    storagePersistResolvedAt = Date.now()
    return result
  } finally {
    storagePersistInFlight = null
  }
}

const readStorageEstimate = async (): Promise<{ quotaBytes: number; usageBytes: number; freeBytes: number } | null> => {
  try {
    if (typeof navigator === 'undefined' || !navigator.storage?.estimate) return null
    const estimate = await navigator.storage.estimate()
    const quotaBytes = Number(estimate?.quota ?? 0)
    const usageBytes = Number(estimate?.usage ?? 0)
    if (!Number.isFinite(quotaBytes) || quotaBytes <= 0) return null
    if (!Number.isFinite(usageBytes) || usageBytes < 0) return null
    return {
      quotaBytes,
      usageBytes,
      freeBytes: quotaBytes - usageBytes,
    }
  } catch {
    return null
  }
}

const shouldAttemptStorageCleanup = (estimate: { quotaBytes: number; usageBytes: number; freeBytes: number } | null): boolean => {
  if (!estimate) return false
  if (estimate.freeBytes <= STORAGE_HEADROOM_MIN_FREE_BYTES) return true
  return (estimate.usageBytes / Math.max(1, estimate.quotaBytes)) >= 0.92
}

export async function ensureStorageHeadroom(
  client: CacheCleanupClient | null | undefined,
  opts?: { activeDatasetIds?: string[] | null }
): Promise<StorageHeadroomResult> {
  if (storageHeadroomInFlight) return await storageHeadroomInFlight
  const run = async (): Promise<StorageHeadroomResult> => {
    const initialEstimate = await readStorageEstimate()
    if (!shouldAttemptStorageCleanup(initialEstimate)) {
      return {
        attempted: false,
        cleaned: false,
        quotaBytes: initialEstimate?.quotaBytes ?? null,
        usageBytes: initialEstimate?.usageBytes ?? null,
        freeBytes: initialEstimate?.freeBytes ?? null,
      }
    }

    const nowMs = Date.now()
    if (
      storageHeadroomLastAttemptAtMs > 0 &&
      (nowMs - storageHeadroomLastAttemptAtMs) < STORAGE_HEADROOM_ATTEMPT_COOLDOWN_MS
    ) {
      return {
        attempted: false,
        cleaned: false,
        quotaBytes: initialEstimate?.quotaBytes ?? null,
        usageBytes: initialEstimate?.usageBytes ?? null,
        freeBytes: initialEstimate?.freeBytes ?? null,
      }
    }
    storageHeadroomLastAttemptAtMs = nowMs

    try {
      const persisted = await navigator.storage?.persisted?.()
      if (persisted === false) {
        await navigator.storage?.persist?.()
      }
    } catch {
      // ignore
    }

    const normalizedActiveDatasetIds = (Array.isArray(opts?.activeDatasetIds) ? opts.activeDatasetIds : [])
      .map((value) => String(value || '').trim())
      .filter(Boolean)

    try {
      if (typeof client?.cache_cleanup_orphan_parquet === 'function') {
        await Promise.resolve(client.cache_cleanup_orphan_parquet({ maxDepth: 8 }))
      }
    } catch {
      // ignore
    }

    try {
      if (typeof client?.cache_cleanup_zero_byte_files === 'function') {
        await Promise.resolve(client.cache_cleanup_zero_byte_files({ maxDepth: 8 }))
      }
    } catch {
      // ignore
    }

    try {
      if (typeof client?.cache_prune_storage === 'function' && initialEstimate?.quotaBytes) {
        const desiredBudgetBytes = Math.max(
          64 * 1024 * 1024,
          Math.min(
            STORAGE_HEADROOM_MAX_PRUNE_BUDGET_BYTES,
            initialEstimate.quotaBytes - STORAGE_HEADROOM_TARGET_FREE_BYTES,
          ),
        )
        await Promise.resolve(client.cache_prune_storage({
          budgetBytes: desiredBudgetBytes,
          activeDatasetIds: normalizedActiveDatasetIds,
        }))
      }
    } catch {
      // ignore
    }

    const nextEstimate = await readStorageEstimate()
    return {
      attempted: true,
      cleaned: Boolean(nextEstimate && initialEstimate && nextEstimate.usageBytes < initialEstimate.usageBytes),
      quotaBytes: nextEstimate?.quotaBytes ?? initialEstimate?.quotaBytes ?? null,
      usageBytes: nextEstimate?.usageBytes ?? initialEstimate?.usageBytes ?? null,
      freeBytes: nextEstimate?.freeBytes ?? initialEstimate?.freeBytes ?? null,
    }
  }
  storageHeadroomInFlight = run()
  try {
    return await storageHeadroomInFlight
  } finally {
    storageHeadroomInFlight = null
  }
}
